import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialFloatOutput, materialVectorOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {VectorMathNodeOperation} from "#material/nodes/converter/vector-math/constants"
import {vectorMathNodeOperation} from "#material/nodes/converter/vector-math/constants"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {vec3} from "#material/types/vector"
import {z} from "zod/v4-mini"

export interface VectorMathSockets extends MaterialNodeSockets {
    inputs: {
        value: "float" // found in inputs: -; found in settings: float,output; inferred from data: float
        vector: "vector" // found in inputs: normal,vector,color,point; found in settings: vector; inferred from data: float3
        vector_001: "vector" // found in inputs: vector,color,float; found in settings: vector; inferred from data: float3
        vector_002: "vector" // found in inputs: -; found in settings: vector,output; inferred from data: float3
    }
    settings: {
        value: "float" // found in inputs: -; found in settings: float,output; inferred from data: float
        vector: "vector" // found in inputs: normal,vector,color,point; found in settings: vector; inferred from data: float3
        vector_001: "vector" // found in inputs: vector,color,float; found in settings: vector; inferred from data: float3
        vector_002: "vector" // found in inputs: -; found in settings: vector,output; inferred from data: float3
        scale: "float" // found in inputs: -; found in settings: float; inferred from data: float
        useClamp: "boolean"
    }
    settingsExtensions: {
        operation?: VectorMathNodeOperation // found in inputs: -; found in settings: string; inferred from data: string
    }
    outputs: {
        vector: "vector"
        value: "float"
    }
}

const TWO_VECTORS = {
    setting: "operation",
    oneOf: [
        undefined,
        "ADD",
        "SUBTRACT",
        "MULTIPLY",
        "DIVIDE",
        "MULTIPLY_ADD",
        "CROSS_PRODUCT",
        "PROJECT",
        "REFLECT",
        "REFRACT",
        "FACEFORWARD",
        "DOT_PRODUCT",
        "DISTANCE",
        "MINIMUM",
        "MAXIMUM",
        "MODULO",
        "WRAP",
        "SNAP",
    ],
} as const

const THREE_VECTORS = {setting: "operation", oneOf: ["MULTIPLY_ADD", "FACEFORWARD", "WRAP"]} as const
const HAS_SCALE = {setting: "operation", oneOf: ["REFRACT", "SCALE"]} as const

const RETURNS_VALUE = {setting: "operation", oneOf: ["DOT_PRODUCT", "DISTANCE", "LENGTH"]} as const

const RETURNS_VECTOR = {
    setting: "operation",
    oneOf: [
        undefined,
        "ADD",
        "SUBTRACT",
        "MULTIPLY",
        "DIVIDE",
        "MULTIPLY_ADD",
        "CROSS_PRODUCT",
        "PROJECT",
        "REFLECT",
        "REFRACT",
        "FACEFORWARD",
        "SCALE",
        "NORMALIZE",
        "ABSOLUTE",
        "MINIMUM",
        "MAXIMUM",
        "FLOOR",
        "CEIL",
        "FRACTION",
        "MODULO",
        "WRAP",
        "SNAP",
        "SINE",
        "COSINE",
        "TANGENT",
    ],
} as const

export const vectorMathDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeVectorMath,
    },
    nodeClass: "VectorMath",
    ui: {
        label: "Vector Math",
        color: "#6e5eb2",
        items: [
            {name: "vector", valueType: "vector", label: "Vector", output: true, visibleWhen: RETURNS_VECTOR},
            {name: "value", valueType: "float", label: "Value", output: true, visibleWhen: RETURNS_VALUE},
            {name: "vector", valueType: "vector", label: "Vector", defaultValue: {x: 0, y: 0, z: 0}, input: true, setting: true},
            {name: "vector_001", valueType: "vector", label: "Vector", defaultValue: {x: 0, y: 0, z: 0}, input: true, setting: true, visibleWhen: TWO_VECTORS},
            {
                name: "vector_002",
                valueType: "vector",
                label: "Vector",
                defaultValue: {x: 0, y: 0, z: 0},
                input: true,
                setting: true,
                visibleWhen: THREE_VECTORS,
            },
            {name: "scale", valueType: "float", label: "Scale", defaultValue: 1, input: false, setting: true, visibleWhen: HAS_SCALE},
            {
                defaultValue: "ADD",
                name: "operation",
                valueType: "string",
                label: "Operation",
                options: [
                    {label: "Add", value: "ADD"},
                    {label: "Subtract", value: "SUBTRACT"},
                    {label: "Multiply", value: "MULTIPLY"},
                    {label: "Divide", value: "DIVIDE"},
                    {label: "Multiply Add", value: "MULTIPLY_ADD"},
                    {label: "Cross Product", value: "CROSS_PRODUCT"},
                    {label: "Project", value: "PROJECT"},
                    {label: "Reflect", value: "REFLECT"},
                    {label: "Refract", value: "REFRACT"},
                    {label: "Faceforward", value: "FACEFORWARD"},
                    {label: "Dot Product", value: "DOT_PRODUCT"},
                    {label: "Distance", value: "DISTANCE"},
                    {label: "Length", value: "LENGTH"},
                    {label: "Scale", value: "SCALE"},
                    {label: "Normalize", value: "NORMALIZE"},
                    {label: "Absolute", value: "ABSOLUTE"},
                    {label: "Minimum", value: "MINIMUM"},
                    {label: "Maximum", value: "MAXIMUM"},
                    {label: "Floor", value: "FLOOR"},
                    {label: "Ceil", value: "CEIL"},
                    {label: "Fraction", value: "FRACTION"},
                    {label: "Modulo", value: "MODULO"},
                    {label: "Wrap", value: "WRAP"},
                    {label: "Snap", value: "SNAP"},
                    {label: "Sine", value: "SINE"},
                    {label: "Cosine", value: "COSINE"},
                    {label: "Tangent", value: "TANGENT"},
                ],
                input: false,
                setting: true,
            },
        ],
        support: {
            cycles: {full: false, remark: "Greater/less than not implemented"},
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({vector: materialVectorOutput, value: materialFloatOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({
                vector: optionalMaterialInput,
                vector_001: optionalMaterialInput,
                vector_002: optionalMaterialInput,
                value: optionalMaterialInput,
            }),
            settings: z.strictObject({
                operation: z.optional(vectorMathNodeOperation),
                value: z.optional(z.number()),
                vector: z.optional(vec3),
                vector_001: z.optional(vec3),
                vector_002: z.optional(vec3),
                scale: z.optional(z.number()),
                useClamp: z.optional(z.boolean()),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<VectorMathSockets>
